import { Menu } from '@/components/ui/menu';
import type { SortOption, SortState } from '@/lib/sorting';

/**
 * The one sort control, in the toolbar beside the filters.
 *
 * It is `Menu` rather than a new widget, for the reason `menu.tsx` gives: a
 * six-item list in a toolbar does not need a portal and a scroll lock. The
 * direction is on the trigger, as an arrow after the key, so the reader can see
 * which way the list points without opening anything, and every option carries
 * the arrow it would open at.
 *
 * A page with one possible order does not draw this. Drawing a menu with one
 * item in it is worse than drawing nothing, since it implies a choice.
 */
export function SortMenu<T>({
  options,
  state,
  onChange,
  className,
}: {
  options: readonly SortOption<T>[];
  state: SortState;
  onChange: (value: string) => void;
  className?: string;
}) {
  if (options.length < 2) return null;
  const arrow = state.direction === 'desc' ? '↓' : '↑';
  return (
    <Menu
      label={`Sort, currently ${
        options.find((option) => option.value === state.key)?.label ?? 'default'
      } ${state.direction === 'desc' ? 'descending' : 'ascending'}`}
      value={state.key}
      onChange={onChange}
      align="end"
      className={className}
      options={options.map((option) => ({
        value: option.value,
        label: option.value === state.key ? `${option.label} ${arrow}` : option.label,
        // The hint on the selected row says what a second press does, which is
        // the one thing about this control that is not obvious.
        hint: option.value === state.key ? 'reverse' : undefined,
      }))}
    />
  );
}
